import { create } from 'zustand';
import { API_BASE } from '../lib/apiBase.js';

export interface Webhook {
  id: string;
  url: string;
  zoneSlug: string;
  eventType: string;
  active: boolean;
  createdAt: string;
  secret?: string; // Only present once at creation
}

export interface WebhookDelivery {
  id: string;
  webhookConfigId: string;
  eventType: string;
  payload: unknown;
  /** 'success' | 'failed' — une ligne par tentative (webhook.processor.ts). */
  status: string | null;
  attempts: number;
  lastError: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

interface WebhookState {
  webhooks: Webhook[];
  deliveries: WebhookDelivery[];
  loading: boolean;
  error: string | null;

  fetchWebhooks: (jwt: string, tenantId: string) => Promise<void>;
  createWebhook: (jwt: string, tenantId: string, payload: { url: string; zoneSlug: string; eventType: string }) => Promise<Webhook | null>;
  revokeWebhook: (jwt: string, tenantId: string, webhookId: string) => Promise<void>;
  fetchDeliveries: (jwt: string, tenantId: string, webhookId: string) => Promise<void>;
}

function authHeaders(jwt: string, tenantId: string): Record<string, string> {
  return { Authorization: `Bearer ${jwt}`, 'X-Tenant-ID': tenantId };
}

export const useWebhookStore = create<WebhookState>((set) => ({
  webhooks: [],
  deliveries: [],
  loading: false,
  error: null,

  fetchWebhooks: async (jwt, tenantId) => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(`${API_BASE}/integration/webhooks`, { headers: authHeaders(jwt, tenantId) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      set({ webhooks: Array.isArray(data) ? data : [] });
    } catch (err) {
      set({ error: 'Impossible de charger les webhooks' });
      console.warn('[webhookStore] fetchWebhooks failed:', err);
    } finally {
      set({ loading: false });
    }
  },

  createWebhook: async (jwt, tenantId, payload) => {
    set({ error: null });
    try {
      const res = await fetch(`${API_BASE}/integration/webhooks`, {
        method: 'POST',
        headers: { ...authHeaders(jwt, tenantId), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const hook = await res.json() as Webhook;
      set((s) => ({ webhooks: [hook, ...s.webhooks] }));
      return hook;
    } catch (err) {
      set({ error: 'Erreur lors de la creation du webhook' });
      console.warn('[webhookStore] createWebhook failed:', err);
      return null;
    }
  },

  revokeWebhook: async (jwt, tenantId, webhookId) => {
    try {
      const res = await fetch(`${API_BASE}/integration/webhooks/${webhookId}`, {
        method: 'DELETE',
        headers: authHeaders(jwt, tenantId),
      });
      if (res.ok || res.status === 204) {
        set((s) => ({ webhooks: s.webhooks.filter((w) => w.id !== webhookId) }));
      }
    } catch (err) {
      console.warn('[webhookStore] revokeWebhook failed:', err);
    }
  },

  fetchDeliveries: async (jwt, tenantId, webhookId) => {
    set({ deliveries: [], error: null });
    try {
      const res = await fetch(`${API_BASE}/integration/webhooks/${webhookId}/deliveries`, {
        headers: authHeaders(jwt, tenantId),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      set({ deliveries: Array.isArray(data) ? data : [] });
    } catch (err) {
      set({ error: 'Impossible de charger les livraisons' });
      console.warn('[webhookStore] fetchDeliveries failed:', err);
    }
  },
}));
